import React from "react";
import moment from "moment";
import { Link } from "react-router-dom";
import { FreeTrial, BoostPlan } from "components/Premium/views/BoostPlans";
import { AppRoutes } from "@ugg/shared/routes/app-routes";
import { useRecurlySubscriptionStatus } from "lib/hooks/premiumHooks";

interface PremiumState {
  isPremium?: boolean;
  premiumVersion?: string;
  isOnFreeTrial?: boolean;
  hasFailedPayment?: boolean;
  paymentFailureDate?: number;
}

interface Subscription {
  cancelAtPeriodEnd?: boolean | null;
  currentPeriodEnd?: number | null;
  status?: string | null;
  isBraintree?: boolean | null;
}

interface UpcomingInvoice {
  chargeAmount?: number | null;
  subtotal?: number | null;
  error?: string | null;
  nextPaymentAttempt?: number | null;
}

interface BillingData {
  subscription?: Subscription | null;
  upcomingInvoice?: UpcomingInvoice | null;
}

interface AccountStatusProps {
  billing?: BillingData | null;
  premium?: PremiumState | null;
}

const AccountStatus: React.FC<AccountStatusProps> = ({ billing, premium }) => {
  const { isPremium, premiumVersion, isOnFreeTrial } = premium || {};
  const { isActive: hasPlusSubscription } = useRecurlySubscriptionStatus();

  const { subscription, upcomingInvoice } = billing || {};

  const { cancelAtPeriodEnd, currentPeriodEnd } = subscription || {};

  const currentPeriodEndDate = currentPeriodEnd && moment(currentPeriodEnd * 1000).format("MMMM DD, YYYY");
  const nextInvoiceDate =
    upcomingInvoice &&
    upcomingInvoice.nextPaymentAttempt &&
    moment(upcomingInvoice.nextPaymentAttempt * 1000).format("MMMM DD, YYYY");
  const nextPaymentAmount = upcomingInvoice && upcomingInvoice.chargeAmount != null && `${upcomingInvoice.chargeAmount}`;
  const nextPaymentSubtotal = upcomingInvoice && upcomingInvoice.subtotal != null && `${upcomingInvoice.subtotal}`;
  const displayDiscount =
    upcomingInvoice &&
    upcomingInvoice.subtotal != null &&
    upcomingInvoice &&
    upcomingInvoice.chargeAmount != null &&
    upcomingInvoice.chargeAmount !== upcomingInvoice.subtotal;

  const displayPlan = () => {
    // No BOOST - should not happen since BoostBillingOverview only renders for isPremium
    if (!isPremium) {
      return null;
    }

    // On free trial
    if (isOnFreeTrial) {
      return (
        <React.Fragment>
          <FreeTrial billingAmount={nextPaymentAmount} />
          <div className="plan-status">
            {cancelAtPeriodEnd && (
              <div>
                <div className="plan-status_sub-text">Your trial will end on:</div>
                <div className="plan-status_main-text">{currentPeriodEndDate}</div>
              </div>
            )}
            {!cancelAtPeriodEnd && (
              <div>
                <div className="plan-status_sub-text">Your next bill is for:</div>
                <div className="plan-status_main-text">
                  {nextPaymentAmount} <span className={"plan-status_sub-text"}>+ tax on </span>
                  {nextInvoiceDate}
                </div>
              </div>
            )}
            <div className="plan-status_btns">
              {cancelAtPeriodEnd ? (
                !hasPlusSubscription && (
                  <Link
                    to={`${AppRoutes.PLUS}?utm_source=boost-settings`}
                    className="group relative px-[12px] py-[12px] w-full w-[215px] rounded-[3px] font-semibold text-[14px] text-white bg-accent-brown-400 border-[1px] border-accent-yellow-400 shadow-[0_4px_2px_0_rgba(0,0,0,0.25)] transition-all text-center block"
                  >
                    <span
                      className="absolute inset-0 rounded-[6px] opacity-0 group-hover:opacity-100 transition-opacity"
                      style={{
                        background:
                          "linear-gradient(0deg, rgba(244, 200, 116, 0.05) 0%, rgba(244, 200, 116, 0.05) 100%), #25254B",
                      }}
                    />
                    <span className="relative">
                      Upgrade to <span className="font-bold text-accent-yellow-400">PLUS</span>
                    </span>
                  </Link>
                )
              ) : (
                <Link to={AppRoutes.SETTINGS_BOOST_CANCEL_PLAN} className="btn-light-blue plan-status_btn">
                  Cancel Subscription
                </Link>
              )}
            </div>
          </div>
        </React.Fragment>
      );
    }

    // Cancelled Boost
    if (cancelAtPeriodEnd) {
      return (
        <React.Fragment>
          <BoostPlan plan={premiumVersion} billingAmount={nextPaymentAmount} />
          <div className="plan-status">
            <div>
              <div className="plan-status_sub-text">Plan will end on:</div>
              <div className="plan-status_main-text">{currentPeriodEndDate}</div>
            </div>
            {!hasPlusSubscription && (
              <div className="plan-status_btns">
                <Link
                  to={`${AppRoutes.PLUS}?utm_source=boost-settings`}
                  className="group relative px-[12px] py-[12px] w-full max-w-[215px] rounded-[3px] font-semibold text-[14px] text-white bg-accent-brown-400 border-[1px] border-accent-yellow-400 shadow-[0_4px_2px_0_rgba(0,0,0,0.25)] transition-all text-center block"
                >
                  <span
                    className="absolute inset-0 rounded-[6px] opacity-0 group-hover:opacity-100 transition-opacity"
                    style={{
                      background: "linear-gradient(0deg, rgba(244, 200, 116, 0.05) 0%, rgba(244, 200, 116, 0.05) 100%), #25254B",
                    }}
                  />
                  <span className="relative">
                    Upgrade to <span className="font-bold text-accent-yellow-400">PLUS</span>
                  </span>
                </Link>
              </div>
            )}
          </div>
        </React.Fragment>
      );
    }

    // Active BOOST
    return (
      <React.Fragment>
        <BoostPlan
          plan={premiumVersion}
          billingAmount={nextPaymentAmount}
          subtotal={nextPaymentSubtotal}
          displayDiscount={displayDiscount}
        />
        <div className="plan-status">
          <div>
            <div className="plan-status_sub-text">{"Plan will renew on:"}</div>
            <div className="plan-status_main-text">{currentPeriodEndDate}</div>
          </div>
          <div className="plan-status_btns">
            <Link to={AppRoutes.SETTINGS_BOOST_CANCEL_PLAN} className="btn-light-blue plan-status_btn">
              Cancel Subscription
            </Link>
          </div>
        </div>
      </React.Fragment>
    );
  };

  return (
    <div className="account-status">
      <div className="current-plan">{displayPlan()}</div>
    </div>
  );
};

export default AccountStatus;
